import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { FirmwareUpdateService } from '../../services/firmware-update.service';
import { ReceiversService } from '../../../shared/services/receivers.service';
import { CompanyOption } from '../../models/firmware-update.model';

export interface ConfigDialogData {
  companies: CompanyOption[];
}

@Component({
  selector: 'app-firmware-config-dialog',
  templateUrl: './firmware-config-dialog.component.html',
  styleUrls: ['./firmware-config-dialog.component.scss']
})
export class FirmwareConfigDialogComponent implements OnInit {
  companies: CompanyOption[] = [];
  filteredCompanies: CompanyOption[] = [];
  companySearchText = '';
  selectedCompany = '';
  selectedApiKey = '';
  receiverOptions: { apiKey: string; name: string }[] = [];

  config = {
    maxConcurrentDevices: 15,
    sendIntervalMs: 300,
    dataTimeoutMs: 2500,
    maxRestartsPerDevice: 3,
  };
  configLoaded = false;
  isLoading = false;
  isSaving = false;

  constructor(
    public dialogRef: MatDialogRef<FirmwareConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfigDialogData,
    private firmwareService: FirmwareUpdateService,
    private receiversService: ReceiversService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.companies = this.data.companies || [];
    this.filteredCompanies = [...this.companies];
  }

  onCompanySearch(text: string): void {
    const search = (text || '').toLowerCase();
    this.filteredCompanies = this.companies.filter((c) =>
      c.name.toLowerCase().includes(search)
    );
  }

  onCompanySelected(event: any): void {
    this.selectedCompany = event.option.value;
    this.selectedApiKey = '';
    this.configLoaded = false;
    this.loadReceivers();
  }

  displayCompanyName(companyId: string): string {
    if (!companyId) return '';
    const match = this.companies.find((c) => c._id === companyId);
    return match ? match.name : companyId;
  }

  loadReceivers(): void {
    const receivers = this.receiversService.receivers || [];
    const companyReceivers = receivers.filter((r) => r.company === this.selectedCompany);
    const seen = new Set<string>();
    this.receiverOptions = [];
    for (const r of companyReceivers) {
      if (r.apiKey && !seen.has(r.apiKey)) {
        seen.add(r.apiKey);
        this.receiverOptions.push({ apiKey: r.apiKey, name: r.name || r.apiKey });
      }
    }
    this.receiverOptions.sort((a, b) => a.name.localeCompare(b.name));
  }

  onReceiverSelected(): void {
    if (!this.selectedApiKey) return;
    this.isLoading = true;
    this.configLoaded = false;
    this.firmwareService.getReceiverConfig(this.selectedApiKey).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success && res.data) {
          this.config = { ...this.config, ...res.data };
        }
        this.configLoaded = true;
      },
      error: () => {
        this.isLoading = false;
        this.configLoaded = true;
      },
    });
  }

  saveConfig(): void {
    if (!this.selectedApiKey) return;
    this.isSaving = true;
    this.firmwareService.updateReceiverConfig(this.selectedApiKey, this.config).subscribe({
      next: (res) => {
        this.isSaving = false;
        if (res.success) {
          this.snackBar.open(
            this.translate.instant('FirmwareUpdate.ConfigSaved'),
            this.translate.instant('Shared.Accept'),
            { duration: 3000 }
          );
          this.dialogRef.close(true);
        }
      },
      error: () => {
        this.isSaving = false;
        this.snackBar.open(
          this.translate.instant('FirmwareUpdate.ConfigSaveError'),
          this.translate.instant('Shared.Accept'),
          { duration: 5000 }
        );
      },
    });
  }
}
